<template>
	<div>
		<ext-dialog
			v-model="value"
			dialog-type="xlarge"
			:title="$t('product')"
			:primaryButtonText="isEditable ? $t('save') : ''"
			@primaryButtonAction="save"
			@closed="close"
		>
			<template #body>
				<v-form ref="form" v-model="valid">
					<product-select
						v-model="variantInfo"
						:label="$t('product')"
						:rules="rules.variant"
						eager
						@change="onVariantChange"
						:disabled="!isEditable"
						hide-details
					/>

					<format-select
						v-model="formatKey"
						:items="formatOptions"
						class="mb-2"
						:label="$t('format')"
						:rules="rules.format"
						:disabled="!variantId || !isEditable"
						:menu-props="{ offsetY: true }"
						@change="reloadBatches"
					/>

					<template v-if="detailsVisible">
						<div v-for="attr in attributeOptions" :key="attr.id" class="attr-row mb-2 pb-2">
							<v-select
								v-if="attr.type === 'multi_select'"
								v-model="attributeValues[attr.id]"
								:items="
									attr.options
										.filter((o) => !attr.allowed || attr.allowed.includes(o._id))
										.map((o) => ({ text: o.value[$lang] || o.value._default, value: o._id }))
								"
								:label="attr.name[$lang] || attr.name._default"
								chips
								multiple
								small-chips
								deletable-chips
								outlined
								hide-details
								append-icon="icon-angle-down"
								:menu-props="{ offsetY: true }"
							/>
							<v-switch
								v-else-if="attr.type === 'boolean'"
								v-model="attributeValues[attr.id]"
								:label="attr.name[$lang] || attr.name._default"
								hide-details
							/>
							<v-menu
								v-else-if="attr.type === 'date'"
								v-model="datePickers[attr.id]"
								:close-on-content-click="false"
								offset-y
								min-width="290"
							>
								<template #activator="{ on, attrs }">
									<v-text-field
										v-bind="attrs"
										v-on="on"
										v-model="attributeValues[attr.id]"
										:label="attr.name[$lang] || attr.name._default"
										readonly
										hide-details
									/>
								</template>
								<v-date-picker v-model="attributeValues[attr.id]" @input="datePickers[attr.id] = false" />
							</v-menu>
							<v-text-field
								v-else
								v-model="attributeValues[attr.id]"
								:label="attr.name[$lang] || attr.name._default"
								outlined
								hide-details
							/>
						</div>

						<v-divider class="my-4" />

						<v-expansion-panels v-model="activePanel" accordion class="ant-card">
							<v-expansion-panel>
								<v-expansion-panel-header expand-icon="icon-angle-down">
									{{ $t("add_from_stock") }}
								</v-expansion-panel-header>
								<v-expansion-panel-content>
									<v-divider class="mb-4"></v-divider>
									<v-simple-table dense class="wide-table">
										<thead>
											<tr>
												<th>{{ $t("lot_no") }}</th>
												<th>{{ $t("measurements") }}</th>
												<th>{{ $t("attributes") }}</th>
												<th>{{ $t("pieces") }}</th>
												<th class="text-right">{{ $t("available") }}</th>
											</tr>
										</thead>
										<tbody>
											<tr v-for="b in filteredBatches" :key="b._rowKey" @click="toggleBatch(b)" style="cursor: pointer">
												<td>{{ b.lotNo }}</td>
												<td>{{ fmtMeas(b) }}</td>
												<td>{{ fmtAttrs(b) }}</td>
												<td>{{ fmtPieces(b) }}</td>
												<td class="text-right">{{ b.available }} {{ currentUnitCode }}</td>
											</tr>
											<tr v-if="!filteredBatches.length">
												<td colspan="4" class="text-center">{{ $t("no_stock_matches_filters") }}</td>
											</tr>
										</tbody>
									</v-simple-table>
								</v-expansion-panel-content>
							</v-expansion-panel>

							<v-expansion-panel>
								<v-expansion-panel-header expand-icon="icon-angle-down">
									{{ $t("add_manually") }}
								</v-expansion-panel-header>
								<v-expansion-panel-content>
									<v-divider class="mb-4"></v-divider>
									<div class="d-flex align-center py-1">
										<span class="subtitle-1"> {{ $t("measurements") }} ({{ dimUnit }}) </span>
									</div>
									<v-divider class="mb-4" />

									<template v-if="manualFields.length === 1">
										<v-row dense class="my-2">
											<v-col cols="4">
												<v-text-field
													v-model.number="manualInput.dimensions[manualFields[0]]"
													:label="$t(manualFields[0])"
													type="number"
													:disabled="isFixedDimension(manualFields[0])"
													hide-details
												/>
											</v-col>
											<v-col cols="2" v-if="dimUnit">
												<v-text-field :value="dimUnit" :label="$t('unit')" disabled hide-details />
											</v-col>
											<v-col cols="3">
												<v-text-field
													v-model.number="manualInput.pieces"
													:label="$t('pieces')"
													type="number"
													hide-details
												/>
											</v-col>
											<v-col cols="1" class="d-flex align-center justify-end">
												<v-btn icon @click="addManualLine">
													<v-icon>icon-plus</v-icon>
												</v-btn>
											</v-col>
										</v-row>
									</template>

									<template v-else>
										<v-row dense class="my-2">
											<v-col v-for="f in manualFields" :key="f" :cols="inputColWidth">
												<v-text-field
													v-model.number="manualInput.dimensions[f]"
													:label="$t(f)"
													type="number"
													:disabled="isFixedDimension(f)"
													hide-details
												/>
											</v-col>
											<v-col :cols="unitColWidth" v-if="dimUnit">
												<v-text-field :value="dimUnit" :label="$t('unit')" disabled hide-details />
											</v-col>
											<v-col :cols="pieceColWidth">
												<v-text-field
													v-model.number="manualInput.pieces"
													:label="$t('pieces')"
													type="number"
													hide-details
												/>
											</v-col>
											<v-col cols="1" class="d-flex align-center justify-end">
												<v-btn icon @click="addManualLine">
													<v-icon>icon-plus</v-icon>
												</v-btn>
											</v-col>
										</v-row>
									</template>
								</v-expansion-panel-content>
							</v-expansion-panel>
						</v-expansion-panels>

						<v-simple-table dense class="mt-4 wide-table">
							<thead>
								<tr>
									<th>{{ $t("lot_no") }}</th>
									<th>{{ $t("measurements") }}</th>
									<th>{{ $t("attributes") }}</th>
									<th>{{ $t("pieces") }}</th>
									<th class="text-right">{{ $t("quantity") }}</th>
									<th class="text-center" style="width: 45px"></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(l, ix) in lines" :key="l.subId || l.tempId || `row_${ix}`">
									<td>{{ l.lotNo || "-" }}</td>
									<td>{{ l.manual ? fmtManual(l) : fmtMeas(l) }}</td>
									<td>{{ fmtAttrs(l) }}</td>
									<td>
										<v-text-field v-model="l.pieces" type="number" :min="0" dense hide-details />
									</td>
									<td class="text-right">{{ l.quantity }} {{ currentUnitCode }}</td>
									<td class="text-center">
										<v-btn icon small @click.stop="removeLine(ix)">
											<v-icon small>icon-cross</v-icon>
										</v-btn>
									</td>
								</tr>
								<tr v-if="!lines.length">
									<td colspan="6" class="text-center">
										{{ $t("no_stocks_added_yet") }}
									</td>
								</tr>
							</tbody>
						</v-simple-table>

						<v-divider class="my-4" />

						<v-text-field
							v-model.number="quantity"
							disabled
							:label="$t('quantity')"
							type="number"
							:min="0"
							:rules="rules.quantity"
							hide-details
						/>
						<v-text-field
							v-model.number="amount"
							:label="$t('unit_price')"
							:rules="rules.amount"
							type="number"
							:min="0"
							:suffix="currencySymbol"
							:disabled="!isEditable"
							hide-details
						/>
					</template>
				</v-form>
			</template>
		</ext-dialog>
	</div>
</template>

<script>
import ExtDialog from "../../../partials/dialogs/ExtDialog.vue";
import ProductSelect from "../../../controls/ProductSelect.vue";
import FormatSelect from "../../../controls/FormatSelect.vue";
import remote from "../../../../data/remote";
import cloneDeep from "lodash.clonedeep";
import { LIN_SCALE, baseLinearUnit, measurementValue } from "../../../mixins/measure.mixin.js";
import uiMixin from "../../../../store/interfaces/ui.mixin";

const round = (n, p = 6) => parseFloat(n.toFixed(p));

export default {
	components: { ExtDialog, ProductSelect, FormatSelect },
	mixins: [uiMixin],
	model: { prop: "value", event: "input" },
	props: {
		value: Boolean,
		item: Object,
		proformaInvoice: Object,
		shipment: Object,
		container: Object,
	},
	data() {
		return {
			valid: false,
			variantInfo: null,
			currentVariant: null,
			variantId: "",
			productId: "",
			formatKey: null,
			formatOptions: [],
			attributeOptions: [],
			attributeValues: {},
			datePickers: {},
			lines: [],
			allBatches: [],
			manualInput: { dimensions: {}, pieces: null },
			quantity: 0,
			amount: 0,
			saving: false,
			prevAuto: 0,
			activePanel: 0,
			attrsHydrated: false,
			rules: {
				variant: [(v) => Boolean(v) || this.$t("$validation.required", { item: this.$t("variant") })],
				format: [(v) => !!v || this.$t("$validation.required", { item: this.$t("format") })],
				quantity: [
					(v) => (!isNaN(v) && Number(v) > 0) || this.$t("$validation.required", { item: this.$t("quantity") }),
				],
				amount: [(v) => !isNaN(v) || this.$t("$validation.required", { item: this.$t("price") })],
			},
		};
	},
	computed: {
		isProforma() {
			return !!this.proformaInvoice;
		},
		isShipment() {
			return !!this.shipment && !!this.container;
		},
		dimUnit() {
			const f = this.selectedFormatOption || {};
			const u = (this.selectedMeasurementDef?.unit || f.unit || "").trim();
			return u || (f.outUnit || f.unitCode || "").trim();
		},
		inputColWidth() {
			const n = this.manualFields.length;
			return n ? Math.floor(8 / n) : 8;
		},
		unitColWidth: () => 1,
		pieceColWidth: () => 2,
		currentUnitCode() {
			return this.selectedFormatOption?.outUnit || this.selectedFormatOption?.unitCode || "";
		},
		isEditable() {
			if (this.isProforma) return this.proformaInvoice.status !== "processed";
			if (this.isShipment) return !["off_load"].includes(this.container.status);
			return true;
		},
		currencySymbol() {
			if (this.isProforma) return this.proformaInvoice.currency?.symbol || "$";
			if (this.isShipment) return this.shipment.currency?.symbol || "$";
			return "$";
		},
		$lang() {
			return this.$i18n.locale;
		},
		filteredBatches() {
			let arr = [...this.allBatches];
			const sel = this.formatOptions.find((o) => o.key === this.formatKey);
			if (sel) {
				const mid = String(sel.measurementId);
				arr = arr.filter((b) => String(b.measurementId) === mid);
			}
			Object.entries(this.attributeValues).forEach(([aid, val]) => {
				if (val == null || val === "" || (Array.isArray(val) && !val.length)) return;
				if (typeof val === "boolean" && val === false) return;
				if (Array.isArray(val)) {
					arr = arr.filter((b) =>
						(b.attributes || []).some(
							(av) =>
								String(av.attributeId?.$oid ?? av.attributeId) === aid && val.map(String).includes(String(av.value))
						)
					);
				} else if (typeof val === "boolean") {
					arr = arr.filter((b) =>
						(b.attributes || []).some(
							(av) => String(av.attributeId?.$oid ?? av.attributeId) === aid && av.value === true
						)
					);
				} else {
					arr = arr.filter((b) =>
						(b.attributes || []).some(
							(av) => String(av.attributeId?.$oid ?? av.attributeId) === aid && String(av.value) === String(val)
						)
					);
				}
			});
			return arr;
		},
		detailsVisible() {
			return this.variantId && this.formatKey;
		},
		selectedFormatOption() {
			return this.formatOptions.find((o) => o.key === this.formatKey);
		},
		selectedMeasurementDef() {
			return (
				this.currentVariant?.measurements.find(
					(m) => String(m._id) === String(this.selectedFormatOption?.measurementId)
				) || null
			);
		},
		manualFields() {
			if (this.selectedFormatOption?.requiredMeasurements?.length)
				return this.selectedFormatOption.requiredMeasurements;
			return ["value"];
		},
		fixedDims() {
			return Object.fromEntries(
				Object.entries(this.selectedMeasurementDef || {}).filter(([k, v]) => k !== "pieces" && v != null)
			);
		},
	},
	watch: {
		selectedFormatOption() {
			const m = this.selectedMeasurementDef;
			this.manualInput.dimensions = {};
			this.manualFields.forEach((f) => {
				this.$set(this.manualInput.dimensions, f, m?.[f] ?? null);
			});
			this.manualInput.pieces = null;
		},
		value(v) {
			if (v) this.init();
		},
		lines: {
			deep: true,
			handler() {
				const fmt = this.selectedFormatOption || {};
				this.lines.forEach((l) => {
					if (!l.manual && (!l.measurements || !l.measurements.length)) {
						l.quantity = l.available;
					} else {
						const m = l.manual ? { ...l, pieces: l.pieces } : { ...l.measurements[0], pieces: l.pieces };
						l.quantity = this.calcAvailable(m, fmt);
					}
				});
				this.autoQuantity();
			},
		},
		activePanel() {
			this.autoQuantity();
		},
		formatOptions(newOpts) {
			if (this.item && newOpts.length && !this.formatKey) {
				const firstLine = (this.item.lines || []).find((l) => !l.manual) || (this.item.lines || [])[0];
				if (firstLine && firstLine.measurementId) {
					const hit = newOpts.find((o) => String(o.measurementId) === String(firstLine.measurementId));
					if (hit) this.$nextTick(() => (this.formatKey = hit.key));
				}
			}
		},
		formatKey(newKey, oldKey) {
			if (!newKey || newKey === oldKey) return;
			if (!this.item?.id) {
				this.lines = [];
			}
			this.reloadBatches();
		},
	},
	methods: {
		safeId(raw) {
			return raw?.$oid ?? raw ?? "";
		},
		calcAvailable(meas, fmtIn = {}) {
			const fmt = fmtIn || {};
			const linUnitSource = meas.unit || fmt.unit || fmt.label || "";
			const linUnit = baseLinearUnit(linUnitSource);
			const factor = LIN_SCALE[linUnit] ?? 1;
			const norm = { ...meas };
			const linearKeys = (fmt.requiredMeasurements || []).filter((k) => !["value", "pieces"].includes(k));
			linearKeys.forEach((k) => {
				if (norm[k] != null) norm[k] = norm[k] * factor;
			});
			const perPiece = measurementValue(norm, fmt);
			return round(perPiece * (norm.pieces ?? 1));
		},
		removeLine(ix) {
			this.lines.splice(ix, 1);
		},
		enrichLines() {
			this.lines = this.lines.map((l) => {
				if (l.manual) {
					return { ...l, unit: this.selectedMeasurementDef?.unit || this.selectedFormatOption?.unit || "" };
				}
				const match = this.allBatches.find(
					(b) =>
						String(b.stockId) === String(l.stockId) &&
						String(b.batchId) === String(l.batchId) &&
						String(b.batchMeasurementId) === String(l.batchMeasurementId)
				);
				if (!match) return l;
				const merged = { ...match, ...l };
				if (merged.pieces == null) merged.pieces = match.pieces ?? 1;
				return merged;
			});
		},
		isFixedDimension(f) {
			return this.fixedDims[f] != null;
		},
		reset() {
			this.variantInfo = null;
			this.currentVariant = null;
			this.variantId = "";
			this.productId = "";
			this.formatKey = null;
			this.formatOptions = [];
			this.attributeOptions = [];
			this.attributeValues = {};
			this.datePickers = {};
			this.lines = [];
			this.allBatches = [];
			this.manualInput = { dimensions: {}, pieces: null };
			this.quantity = 0;
			this.amount = 0;
			this.prevAuto = 0;
			this.activePanel = 0;
			this.attrsHydrated = false;
			this.$refs.form?.reset();
		},
		init() {
			this.reset();
			if (this.item) {
				const it = cloneDeep(this.item);
				this.variantInfo = { productId: it.productId, variantId: it.variantId, name: it.name };
				this.amount = it.amount;
				this.lines = cloneDeep(it.lines || []);
				this.quantity = it.quantity || 0;
				this.onVariantChange(this.variantInfo);
			}
		},
		autoQuantity() {
			const total = this.lines.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0);
			this.quantity = round(total);
		},
		fmtMeas(o) {
			if (o.manual) return this.fmtManual(o);
			const m = o.measurements?.[0] || o;
			const fmt = this.selectedFormatOption || {};
			const unitLabel = (m.unit || fmt.unit || o.unit || "").trim() || (fmt.outUnit || fmt.unitCode || "").trim();
			if (m.value != null && m.length == null && m.width == null && m.height == null) {
				return `${m.value}${unitLabel ? " " + unitLabel : ""}`.trim();
			}
			const allowed = ["length", "width", "height", "value"];
			const keys = (fmt.requiredMeasurements || []).filter((k) => allowed.includes(k) && m[k] != null);
			if (!keys.length) keys.push(...allowed.filter((k) => m[k] != null));
			const dimText = keys.map((k) => m[k]).join("×");
			return `${dimText}${unitLabel ? " " + unitLabel : ""}`.trim();
		},
		fmtPieces(o) {
			if (o.pieces != null) return o.pieces;
			if (o.measurements?.length) return o.measurements.reduce((s, m) => s + (m.pieces || 1), 0);
			return 1;
		},
		fmtManual(l) {
			const fmt = this.selectedFormatOption || {};
			const keys = this.manualFields.filter((k) => k !== "pieces");
			const dimText = keys
				.map((k) => l[k])
				.filter((v) => v != null)
				.join("×");
			const unitLabel = this.dimUnit ? ` ${this.dimUnit}` : "";
			return `${dimText}${unitLabel}`.trim();
		},
		fmtAttrs(obj) {
			if (!obj.attributes?.length) return "-";
			const parts = [];
			const lang = this.$lang;
			obj.attributes.forEach((av) => {
				const def = this.attributeOptions.find((a) => String(a.id) === String(av.attributeId));
				if (!def) return;
				const label = def.name[lang] || def.name._default;
				if (def.type === "boolean" && av.value === true) parts.push(label);
				else if (def.type === "multi_select") {
					const vals = Array.isArray(av.value) ? av.value : [av.value];
					const names = vals
						.map((v) => {
							const opt =
								def.options.find((o) => String(o._id) === String(v)) ||
								def.options.find((o) => (o.value[lang] || o.value._default) === v);
							return opt ? opt.value[lang] || opt.value._default : "";
						})
						.filter(Boolean);
					if (names.length) parts.push(`${label}: ${names.join(", ")}`);
				} else if (def.type === "date" && av.value) {
					parts.push(`${label}: ${new Date(av.value).toLocaleDateString()}`);
				} else if (av.value != null && av.value !== "") {
					parts.push(`${label}: ${av.value}`);
				}
			});
			return parts.length ? parts.join("; ") : "-";
		},
		onVariantChange(o) {
			if (!o) return;
			this.productId = o.productId;
			this.variantId = o.variantId;

			if (!this.item?.id) {
				this.formatKey = null;
				this.lines = [];
				this.manualInput = { dimensions: {}, pieces: null };
				this.quantity = 0;
				this.amount = 0;
				this.attributeOptions = [];
				this.attributeValues = {};
				this.allBatches = [];
			}

			remote.products.find(o.productId, {
				onSuccess: (p) => {
					const v = p.variants.find((x) => x._id === o.variantId) || { measurements: [], attributes: [] };
					this.currentVariant = v;

					const seen = new Set();
					this.formatOptions = v.measurements
						.map((m, idx) => {
							const baseUnit = (m.format.includes("(") ? m.format.split("(")[0] : m.format).trim();
							const dims = [];
							if (m.length != null) dims.push(m.length);
							if (m.width != null) dims.push(m.width);
							if (m.height != null) dims.push(m.height);
							let label = m.format.includes("(") ? m.format : baseUnit;
							if (m.value != null && !dims.length) label = `${m.value} ${baseUnit}`.trim();
							const key = `${label.toLowerCase()}_${baseUnit}_${m._id}`;
							if (seen.has(key)) return null;
							seen.add(key);
							return {
								key: idx + 1,
								formatId: m.formatId,
								measurementId: m._id,
								label,
								unit: m.unit || "",
								outUnit: baseUnit,
								requiredMeasurements: (() => {
									const inside = (m.format.match(/\(([^)]+)\)/) || [, ""])[1];
									return inside
										.replace(/\s*[x×*]\s*/gi, "*")
										.split("*")
										.map((s) => s.trim().toLowerCase())
										.filter(Boolean);
								})(),
							};
						})
						.filter(Boolean);

					const allowedMap = new Map((v.attributes || []).map((a) => [String(a.attributeId), a.value]));
					this.attributeOptions = (p.attributes || []).map((a) => ({
						id: String(a._id),
						name: a.name,
						type: a.type,
						options: a.options || [],
						allowed: allowedMap.get(String(a._id)),
					}));
					this.attributeValues = {};
					this.attributeOptions.forEach((a) => {
						if (a.type === "boolean") this.$set(this.attributeValues, a.id, false);
						else if (a.type === "multi_select") this.$set(this.attributeValues, a.id, []);
						else this.$set(this.attributeValues, a.id, null);
					});

					this.reloadBatches();
				},
			});
		},

		reloadBatches() {
			this.allBatches = [];
			if (!this.variantId) return;

			remote.stocks.filter({
				filter: { variantId: this.variantId, view: "full", positiveOnly: true },
				onSuccess: (res) => {
					const list = Array.isArray(res) ? res : res.items || [];
					const sel = this.selectedFormatOption || {};
					const selectedMeasId = String(sel.measurementId || "");
					const unitFor = (mid) => {
						const def = this.currentVariant?.measurements.find((m) => String(m._id) === String(mid));
						return def?.unit || sel.unit || "";
					};
					const rows = [];
					list.forEach((s) => {
						const stockId = s._id || s.id;
						const stockAttrs = (s.product && s.product.attributes) || [];
						const measIdAtStock = s.product?.measurementId || selectedMeasId;
						const lot = s.lotNo || "";
						const batches = s.batches || [];
						if (!batches.length) {
							rows.push({
								_rowKey: `${stockId}_total`,
								subId: `${stockId}_total`,
								stockId,
								batchId: undefined,
								measurementId: String(measIdAtStock),
								batchMeasurementId: undefined,
								lotNo: lot,
								measurements: [],
								unit: unitFor(measIdAtStock),
								pieces: 1,
								attributes: JSON.parse(JSON.stringify(stockAttrs)),
								available: Number(s.product?.totalQuantity ?? 0),
							});
							return;
						}
						batches.forEach((b) => {
							const lotNo = lot || b.lotNo || b.measurement_no || "";
							const ms = Array.isArray(b.measurements) ? b.measurements : [];
							if (ms.length) {
								ms.forEach((m, idx) => {
									const mid = String(m.measurementId || measIdAtStock);
									const withUnit = { ...m, unit: unitFor(mid) };
									const rk = `${stockId}_${b._id || b.id}_${idx}`;
									rows.push({
										_rowKey: rk,
										subId: rk,
										stockId,
										batchId: b._id || b.id,
										measurementId: mid,
										batchMeasurementId: m._id,
										lotNo,
										measurements: [withUnit],
										unit: withUnit.unit,
										pieces: m.pieces ?? 1,
										attributes: JSON.parse(JSON.stringify(stockAttrs)),
										available: this.calcAvailable(withUnit, sel),
									});
								});
							} else {
								const rk = `${stockId}_${b._id || b.id}_q`;
								rows.push({
									_rowKey: rk,
									subId: rk,
									stockId,
									batchId: b._id || b.id,
									measurementId: String(measIdAtStock),
									batchMeasurementId: undefined,
									lotNo,
									measurements: [],
									unit: unitFor(measIdAtStock),
									pieces: 1,
									attributes: JSON.parse(JSON.stringify(stockAttrs)),
									available: Number(b.quantity || 0),
								});
							}
						});
					});
					this.allBatches = rows;
					this.enrichLines();
					this.autoQuantity();
					if (this.item?.id && !this.attrsHydrated) {
						this.hydrateAttributeValuesFromLines();
						this.attrsHydrated = true;
					}
				},
			});
		},

		hydrateAttributeValuesFromLines() {
			const norm = (x) => String(x && (x.$oid || x));
			const line = this.lines.find((l) => Array.isArray(l.attributes) && l.attributes.length);
			if (!line) return;
			this.attributeOptions.forEach((opt) => {
				const hit = (line.attributes || []).find((av) => norm(av.attributeId) === norm(opt.id));
				if (!hit) return;
				if (opt.type === "multi_select") {
					const arr = Array.isArray(hit.value) ? hit.value : [hit.value];
					const ids = arr
						.map((v) => {
							const m =
								opt.options.find((o) => norm(o._id) === norm(v)) ||
								opt.options.find((o) => (o.value[this.$lang] || o.value._default) === v);
							return m ? norm(m._id) : null;
						})
						.filter(Boolean);
					this.$set(this.attributeValues, opt.id, ids);
				} else if (opt.type === "boolean") {
					this.$set(this.attributeValues, opt.id, !!hit.value);
				} else {
					this.$set(this.attributeValues, opt.id, hit.value);
				}
			});
		},

		toggleBatch(b) {
			if (this.activePanel !== 0) return;
			const ix = this.lines.findIndex((l) => l.subId === b.subId);
			if (ix === -1) this.lines.push({ ...b, quantity: 1, pieces: b.pieces ?? 1 });
			else this.lines.splice(ix, 1);
		},

		addManualLine() {
			if (this.activePanel !== 1) return;
			const dims = this.manualInput.dimensions;
			const pieces = Number(this.manualInput.pieces) || 0;
			if (!pieces) return;
			const meas = { ...dims, pieces };
			const qty = this.calcAvailable(meas, this.selectedFormatOption || {});
			this.lines.push({
				tempId: `m${Date.now()}`,
				manual: true,
				...meas,
				quantity: qty,
				pieces,
				measurementId: this.selectedFormatOption.measurementId,
				unit: this.dimUnit,
			});
			this.manualInput.pieces = null;
			this.manualFields.forEach((f) => {
				if (!this.isFixedDimension(f)) this.manualInput.dimensions[f] = null;
			});
		},

		save() {
			if (this.item?.id && this.lines.length === 0) {
				if (this.isProforma) {
					remote.proformaInvoices.products.remove(this.proformaInvoice.id, this.item.id, {
						onSuccess: () => {
							this.$emit("deleted");
							this.showSnackBar({ message: this.$t("$message.deleted", { item: this.$t("product") }) });
							this.close();
						},
						onFail: () => {
							this.showAlertDialog({ message: this.$t("delete_failed") });
						},
					});
				} else if (this.isShipment) {
					remote.shipments.containers.products.delete(this.shipment.id, this.container.id, this.item.id, {
						onSuccess: () => {
							this.$emit("deleted");
							this.showSnackBar({ message: this.$t("$message.deleted", { item: this.$t("product") }) });
							this.close();
						},
						onFail: () => {
							this.showAlertDialog({ message: this.$t("delete_failed") });
						},
					});
				}
				return;
			}
			if (this.activePanel === 1) {
				const typedDims = this.manualFields
					.filter((f) => !this.isFixedDimension(f))
					.some((f) => this.manualInput.dimensions[f] != null && this.manualInput.dimensions[f] !== "");
				const typedPieces = this.manualInput.pieces != null && this.manualInput.pieces !== "";
				if (typedDims || typedPieces) {
					this.showAlertDialog({ message: this.$t("warn_unsaved_measurement") });
					return;
				}
			}
			if (!this.isEditable || this.saving) return;
			if (!this.$refs.form.validate()) return;

			const rawName = (this.variantInfo && (this.variantInfo.name || this.variantInfo.label)) || "";
			const name =
				rawName && typeof rawName === "object" ? rawName : { _default: rawName || "", [this.$lang]: rawName || "" };

			const fmt = this.formatOptions.find((o) => o.key === this.formatKey) || {};
			const payload = {
				productId: this.productId,
				variantId: this.variantId,
				name,
				unitCode: fmt?.outUnit || fmt?.unitCode || "",
				quantity: this.quantity,
				amount: this.amount || 0,
				lines: this.lines.map((l) =>
					l.manual
						? {
								manual: true,
								quantity: l.quantity,
								measurementId: l.measurementId,
								pieces: l.pieces,
								length: l.length,
								width: l.width,
								height: l.height,
								value: l.value,
						  }
						: {
								stockId: l.stockId,
								batchId: l.batchId,
								measurementId: l.measurementId,
								batchMeasurementId: l.batchMeasurementId,
								pieces: l.pieces,
								quantity: l.quantity,
						  }
				),
			};

			this.saving = true;
			const done = () => {
				this.saving = false;
				this.close();
			};

			if (this.isProforma) {
				if (this.item?.id) {
					remote.proformaInvoices.products.update(this.proformaInvoice.id, this.item.id, payload, {
						onSuccess: (r) => {
							this.$emit("saved", r);
							this.showSnackBar({ message: this.$t("$message.updated", { item: this.$t("product") }) });
							done();
						},
						onFail: () => (this.saving = false),
					});
				} else {
					remote.proformaInvoices.products.createGroup(this.proformaInvoice.id, [payload], {
						onSuccess: (resList) => {
							const r = Array.isArray(resList) ? resList[0] : resList;
							this.$emit("saved", r);
							this.showSnackBar({ message: this.$t("$message.created", { item: this.$t("product") }) });
							done();
						},
						onFail: () => (this.saving = false),
					});
				}
			} else if (this.isShipment) {
				if (this.item?.id) {
					remote.shipments.containers.products.update(
						this.shipment.id,
						this.container.id,
						{ ...payload, id: this.item.id },
						{
							onSuccess: (r) => {
								this.$emit("saved", r);
								this.showSnackBar({ message: this.$t("$message.updated", { item: this.$t("product") }) });
								done();
							},
							onFail: () => (this.saving = false),
						}
					);
				} else {
					remote.shipments.containers.products.create(this.shipment.id, this.container.id, payload, {
						onSuccess: (r) => {
							const res = Array.isArray(r) ? r[0] : r;
							this.$emit("saved", res);
							this.showSnackBar({ message: this.$t("$message.created", { item: this.$t("product") }) });
							done();
						},
						onFail: () => (this.saving = false),
					});
				}
			}
		},
		close() {
			this.$emit("input", false);
		},
	},
};
</script>

<style scoped>
.wide-table td {
	white-space: normal;
}
.attr-row:first-of-type {
	margin-top: 16px;
}
</style>
